/**
 * The live drafts of the artifacts the streaming writer is making (plan 7.1):
 * a module store fed by the chat stream's `artifact-draft` events, outside the
 * assistant-ui message content, so a code delta never re-renders the thread.
 * Only the building card and the canvas panel of that one build subscribe.
 *
 * Keyed by the tool call id, with a secondary index by artifact id (the panel
 * knows only the artifact). Readers see a published copy that changes at most
 * ten times a second, however fast events arrive. A build is dropped 60 s
 * after it ends, or earlier once its tool result arrived and the thread is
 * left. Nothing is persisted.
 */
import { useSyncExternalStore } from 'react';
import type { ArtifactDraftEvent } from '@tabbio/types';
import {
  isDraftTerminal,
  reduceArtifactDraft,
  stopArtifactDraft,
  type ArtifactDraft,
} from './artifact-draft-model';

export type { ArtifactDraft } from './artifact-draft-model';

export const ARTIFACT_DRAFT_STORE_LIMITS = {
  /** Readers are notified at most this often (10 times a second). */
  notifyIntervalMs: 100,
  /** A finished or failed build is dropped this long after its last event. */
  clearAfterEndMs: 60_000,
  /** First-seen times kept for the writer-off card's elapsed seconds. */
  callTimesMax: 200,
} as const;

/** The builds as events left them; readers never see this one directly. */
const entries = new Map<string, ArtifactDraft>();
/** What readers see: updated from `entries` at each notification. */
const published = new Map<string, ArtifactDraft>();
const publishedByArtifact = new Map<string, string>();
const dirty = new Set<string>();
const clearTimers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();
const callFirstSeen = new Map<string, number>();

let notifyTimer: ReturnType<typeof setTimeout> | null = null;
let lastNotifyAt = -Infinity;

function flush() {
  if (notifyTimer !== null) {
    clearTimeout(notifyTimer);
    notifyTimer = null;
  }
  if (dirty.size === 0) return;
  for (const toolCallId of dirty) {
    const entry = entries.get(toolCallId);
    const previous = published.get(toolCallId);
    if (entry) {
      published.set(toolCallId, entry);
      // The newest build of an artifact wins its slot (an edit after a create).
      if (entry.artifactId) publishedByArtifact.set(entry.artifactId, toolCallId);
    } else {
      published.delete(toolCallId);
    }
    if (previous?.artifactId && publishedByArtifact.get(previous.artifactId) === toolCallId && !entry) {
      publishedByArtifact.delete(previous.artifactId);
    }
  }
  dirty.clear();
  lastNotifyAt = Date.now();
  for (const listener of [...listeners]) listener();
}

/** Leading and trailing throttle: at most one notification per interval. */
function scheduleNotify() {
  if (notifyTimer !== null) return;
  const wait = lastNotifyAt + ARTIFACT_DRAFT_STORE_LIMITS.notifyIntervalMs - Date.now();
  if (wait <= 0) {
    flush();
    return;
  }
  notifyTimer = setTimeout(flush, wait);
}

function scheduleClear(toolCallId: string) {
  const existing = clearTimers.get(toolCallId);
  if (existing) clearTimeout(existing);
  clearTimers.set(
    toolCallId,
    setTimeout(() => {
      clearTimers.delete(toolCallId);
      removeDraft(toolCallId);
    }, ARTIFACT_DRAFT_STORE_LIMITS.clearAfterEndMs),
  );
}

function removeDraft(toolCallId: string) {
  if (!entries.delete(toolCallId)) return;
  const timer = clearTimers.get(toolCallId);
  if (timer) {
    clearTimeout(timer);
    clearTimers.delete(toolCallId);
  }
  dirty.add(toolCallId);
  scheduleNotify();
}

function setDraft(next: ArtifactDraft) {
  entries.set(next.toolCallId, next);
  dirty.add(next.toolCallId);
  if (isDraftTerminal(next) && !clearTimers.has(next.toolCallId)) scheduleClear(next.toolCallId);
  scheduleNotify();
}

/**
 * Applies one `artifact-draft` event. Returns the build as it stands after
 * the event, or null when the event was ignored (older or repeated `seq`, or
 * after the build ended).
 */
export function applyArtifactDraftEvent(event: ArtifactDraftEvent): ArtifactDraft | null {
  const current = entries.get(event.toolCallId);
  const next = reduceArtifactDraft(current, event, Date.now());
  if (!next || next === current) return null;
  setDraft(next);
  return next;
}

/** The call's tool result arrived on the live stream. */
export function markArtifactDraftResult(toolCallId: string) {
  const current = entries.get(toolCallId);
  if (!current || current.resultArrived) return;
  setDraft({ ...current, resultArrived: true });
}

/**
 * The user stopped the reply: the stream closed before these builds ended,
 * so they never will. They read as stopped ("Stopped before it finished").
 */
export function stopArtifactDrafts(toolCallIds: Iterable<string>) {
  const now = Date.now();
  for (const toolCallId of toolCallIds) {
    const current = entries.get(toolCallId);
    if (!current) continue;
    const next = stopArtifactDraft(current, now);
    if (next !== current) setDraft(next);
  }
}

/**
 * The thread is left: builds that are over, or whose result already arrived,
 * are no longer needed (their cards read the result). A build still running
 * stays until it ends.
 */
export function releaseSettledArtifactDrafts() {
  for (const [toolCallId, entry] of entries) {
    if (entry.resultArrived || isDraftTerminal(entry)) removeDraft(toolCallId);
  }
}

/**
 * When the card of a creating call was first drawn, for the elapsed seconds
 * of the writer-off card; stable across remounts of the card.
 */
export function artifactCallFirstSeen(toolCallId: string): number {
  const seen = callFirstSeen.get(toolCallId);
  if (seen !== undefined) return seen;
  const now = Date.now();
  callFirstSeen.set(toolCallId, now);
  if (callFirstSeen.size > ARTIFACT_DRAFT_STORE_LIMITS.callTimesMax) {
    const oldest = callFirstSeen.keys().next().value;
    if (oldest !== undefined) callFirstSeen.delete(oldest);
  }
  return now;
}

export function readArtifactDraft(toolCallId: string | null | undefined): ArtifactDraft | null {
  return toolCallId ? published.get(toolCallId) ?? null : null;
}

export function readArtifactDraftFor(artifactId: string | null | undefined): ArtifactDraft | null {
  if (!artifactId) return null;
  const toolCallId = publishedByArtifact.get(artifactId);
  return toolCallId ? published.get(toolCallId) ?? null : null;
}

export function subscribeArtifactDrafts(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const serverSnapshot = () => null;

/** The build of one tool call, or null. Re-renders only when that build changes. */
export function useArtifactDraft(toolCallId: string | null | undefined): ArtifactDraft | null {
  return useSyncExternalStore(
    subscribeArtifactDrafts,
    () => readArtifactDraft(toolCallId),
    serverSnapshot,
  );
}

/** The newest build of one artifact, or null (the canvas panel). */
export function useArtifactDraftFor(artifactId: string | null | undefined): ArtifactDraft | null {
  return useSyncExternalStore(
    subscribeArtifactDrafts,
    () => readArtifactDraftFor(artifactId),
    serverSnapshot,
  );
}

export function resetArtifactDraftsForTests() {
  if (notifyTimer !== null) clearTimeout(notifyTimer);
  notifyTimer = null;
  lastNotifyAt = -Infinity;
  for (const timer of clearTimers.values()) clearTimeout(timer);
  clearTimers.clear();
  entries.clear();
  published.clear();
  publishedByArtifact.clear();
  dirty.clear();
  listeners.clear();
  callFirstSeen.clear();
}
